import { useMemo, useRef, useState } from 'react';

import {
  ComputerUserIcon,
  Delete02Icon,
  DownloadSquare01Icon,
  Link01Icon,
  QuestionIcon,
  SentIcon,
  UserSwitchIcon,
} from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { Tab, Tabs } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';
import { IconComponent } from 'components/Icon/IconComponent';
import LinkShare from 'components/LinkShare/LinkShare';

import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectUser } from 'api/user-slice';

import { CandidateAnswers } from '../CandidateAnswers/CandidateAnswers';
import { RefereeMetadataDetailsModal } from '../RefereeMetadataDetailsModal/RefereeMetadataDetailsModal';
import { ReplaceRefereeModal } from '../ReplaceRefereeModal/ReplaceRefereeModal';
import { RefereeDetails } from './../RefereeDetails/RefereeDetails';
import { RefereeReport } from './../RefereeReport/RefereeReport';
import styles from './Referee.module.scss';

export const Referee = ({
  candidate,
  check,
  company,
  referee,
  canAccessResults = true,
  messagePopUp = () => {},
  updateReferee = () => {},
  deleteReferee = () => {},
  cancelReferee = () => {},
  setLoading = () => {},
  getPdfReport = () => {},
  setShowRequestAlert = () => {},
  onNewRequest = () => {},
  sendReplacementRequest = () => {},
  onClose = () => {},
}) => {
  const [tab, setTab] = useState();

  const [showDeletionAlert, setShowDeletionAlert] = useState(false);
  const [showMetadataDetails, setShowMetadaDetails] = useState(false);
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const user = useSelector(selectUser);
  const answered_on_behalf_of = useMemo(() => user.id, [user]);
  const companyChecks = useSelector(selectCompanyChecks);

  const handleUpdate = async data => {
    setLoading(true);
    await updateReferee(data.id, data);
    setLoading(false);
    messagePopUp('Referee updated', MESSAGE_STATE_SUCCESS);
    if (!referee.completed_at) setShowRequestAlert(true);
  };

  const handleDownloadReport = () => {
    const filename = `${candidate.first_name}_${candidate.last_name}-${referee.first_name}_${referee.last_name}.pdf`;
    getPdfReport(referee.id, filename);
  };

  const handleDeletion = () => {
    setShowDeletionAlert(false);
    onClose();
    deleteReferee(referee.id);
  };

  const handleRefereeCancelation = () => {
    setShowReplacementModal(false);
    onClose();
    cancelReferee(referee.id);
  };

  const handleReplacementRequest = async message => {
    setShowReplacementModal(false);
    await sendReplacementRequest(referee.id, message);
  };

  const formUrl = useMemo(() => {
    const companyCheck = companyChecks.find(cc => cc.type === check.type);
    return companyCheck?.configuration?.use_new_form
      ? `${process.env.REACT_APP_WEB_URL}/form_submission/new_referee/`
      : `${process.env.REACT_APP_WEB_URL}/form_submission/referee/`;
  }, [companyChecks]);

  const iconConfigs = useMemo(
    () => [
      {
        icon: QuestionIcon,
        tip: 'Answer on behalf',
        onClick: () => {
          window.open(`${formUrl}${referee.token}/${answered_on_behalf_of}`, '_blank');
        },
        disabled: referee.completed_at,
      },
      {
        icon: Link01Icon,
        tip: 'Link',
        onClick: () => setShowLink(true),
        disabled: !referee.completed_at,
      },
      {
        icon: UserSwitchIcon,
        tip: 'Ask candidate to replace referee',
        onClick: () => setShowReplacementModal(true),
      },
      {
        icon: SentIcon,
        tip: 'Send New Request to Referee',
        onClick: onNewRequest,
        disabled: referee.completed_at,
      },
      {
        icon: ComputerUserIcon,
        tip: 'Metadata details',
        onClick: () => setShowMetadaDetails(true),
      },
      {
        icon: DownloadSquare01Icon,
        tip: 'Download Form',
        onClick: handleDownloadReport,
        disabled: !referee.completed_at || !canAccessResults,
      },
      {
        icon: Delete02Icon,
        type: 'error',
        tip: 'Delete',
        onClick: () => setShowDeletionAlert(true),
      },
    ],
    [referee.token, referee.completed_at, answered_on_behalf_of],
  );

  return (
    <div className={styles.root}>
      <Alert
        show={showDeletionAlert}
        title={`Are you sure you want to delete ${referee?.first_name}?`}
        message="Referee deletion cannot be undone. Once deleted, we will adjust the number of referees for this candidate"
        ok="Delete"
        type="error"
        onOk={handleDeletion}
        icon={Delete02Icon}
        showConfirmationInput
        cancel="Cancel"
        onCancel={() => setShowDeletionAlert(false)}
      />
      <RefereeMetadataDetailsModal
        candidate={candidate}
        referee={referee}
        referenceCheck={check}
        visible={showMetadataDetails}
        onClose={() => setShowMetadaDetails(false)}
      />
      <ReplaceRefereeModal
        referee={referee}
        visible={showReplacementModal}
        onClose={() => setShowReplacementModal(false)}
        onCancel={handleRefereeCancelation}
        onReplacementRequest={handleReplacementRequest}
      />
      <div className={styles.header}>
        <div className={cn(styles.details, 'u-margin-right')}>
          <div className={styles.title}>REFEREE</div>
          <div className={styles.name}>{referee.name}</div>
        </div>
        <div className={styles.actions}>
          <LinkShare
            open={showLink}
            link={`${process.env.REACT_APP_WEB_URL}/company/report/reference/${referee.token}`}
            title="Share this report"
            onClose={() => setShowLink(false)}
          />
          {iconConfigs.map(({ icon, type = 'secondary', tip, onClick, disabled }, index) => (
            <IconComponent
              key={index}
              icon={icon}
              type={type}
              border={false}
              noBackground
              strokeWidth={1.5}
              size="medium"
              tip={tip}
              onClick={onClick}
              disabled={disabled}
            />
          ))}
        </div>
      </div>
      <Tabs
        activeKey={tab}
        defaultActiveKey={1}
        onSelect={setTab}
        className="tabs padding-small no-padding"
        mountOnEnter={true}>
        <Tab eventKey={1} title="Details">
          <RefereeDetails
            referee={referee}
            company={company}
            onSubmit={handleUpdate}
            setShowRequestAlert={setShowRequestAlert}
          />
        </Tab>
        <Tab eventKey={2} title="Candidate" disabled={!candidate.submitted_at && !referee.manual}>
          <CandidateAnswers candidate={candidate} referee={referee} setLoading={setLoading} />
        </Tab>
        {canAccessResults && (
          <Tab eventKey={3} title="Report" disabled={!referee.completed_at}>
            <RefereeReport
              candidate={candidate}
              referee={referee}
              referenceCheck={check}
              setLoading={setLoading}
            />
          </Tab>
        )}
      </Tabs>
    </div>
  );
};
